import type { AgentStatus } from '@agents/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Icon } from '../components/icons';
import { useCan } from '../components/shell';
import {
  AgentAvatar,
  AgentStatusPill,
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  SearchInput,
  Segmented,
  TableSkeleton,
} from '../components/ui';
import { useDocumentTitle } from '../lib/document-title';
import { dateTime, relativeTime } from '../lib/format';
import { useAgents } from '../lib/queries';

/** Below this many agents a filter bar is noise. */
const FILTER_THRESHOLD = 6;

type StatusFilter = 'all' | AgentStatus;

export function AgentsPage() {
  useDocumentTitle('Agentes');
  const can = useCan();
  const agents = useAgents();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');

  const items = agents.data ?? [];
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(
      (a) =>
        (status === 'all' || a.status === status) &&
        (!q || `${a.name} ${a.description}`.toLowerCase().includes(q)),
    );
  }, [items, query, status]);
  const count = (s: AgentStatus) => items.filter((a) => a.status === s).length;

  const createAction = can('agent.create') ? (
    <Link to="/agentes/nuevo" className="btn btn-primary">
      <Icon name="plus" />
      Crear agente
    </Link>
  ) : null;

  return (
    <>
      <PageHeader
        title="Agentes"
        description="Define qué hace cada agente, publícalo y ejecútalo con las herramientas que autorices."
        actions={items.length ? createAction : null}
      />
      {agents.isPending && <TableSkeleton rows={5} />}
      {agents.isError && <ErrorState error={agents.error} onRetry={() => void agents.refetch()} />}
      {agents.data?.length === 0 &&
        (can('agent.create') ? (
          <EmptyState
            icon="node"
            title="Crea tu primer agente"
            action={createAction}
            flow={[
              { title: 'Crear', text: 'Define qué hace, con qué tono y qué no debe hacer.' },
              { title: 'Publicar', text: 'Congela una versión estable para usarla.' },
              { title: 'Ejecutar', text: 'Pídele una tarea y revisa cómo la resolvió.' },
            ]}
          >
            Un agente combina instrucciones, un modelo y las herramientas que autorices. Queda listo para
            trabajar en tres pasos.
          </EmptyState>
        ) : (
          <EmptyState icon="node" title="Aún no hay agentes en este espacio">
            Cuando un administrador publique un agente, aparecerá aquí para que puedas ejecutarlo y revisar
            sus resultados.
          </EmptyState>
        ))}
      {items.length > 0 && (
        <>
          {items.length > FILTER_THRESHOLD && (
            <div className="toolbar">
              <SearchInput
                label="Buscar agentes"
                placeholder="Buscar por nombre o descripción"
                value={query}
                onChange={setQuery}
              />
              <Segmented<StatusFilter>
                label="Filtrar por estado"
                value={status}
                onChange={setStatus}
                options={[
                  { value: 'all', label: 'Todos', count: items.length },
                  { value: 'active', label: 'Activos', count: count('active') },
                  { value: 'draft', label: 'Borradores', count: count('draft') },
                  { value: 'paused', label: 'Pausados', count: count('paused') },
                ]}
              />
              <span className="count">
                {visible.length === items.length
                  ? `${items.length} en total`
                  : `${visible.length} de ${items.length}`}
              </span>
            </div>
          )}
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Agente</th>
                  <th scope="col">Estado</th>
                  <th scope="col" data-optional>
                    Versión
                  </th>
                  <th scope="col" data-optional data-wide>
                    Modelo
                  </th>
                  <th scope="col" className="num" data-optional data-wide>
                    Herramientas
                  </th>
                  <th scope="col" data-optional>
                    Última ejecución
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((a) => (
                  <tr
                    key={a.id}
                    data-href
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('a, button')) return;
                      void navigate({ to: '/agentes/$agentId', params: { agentId: a.id } });
                    }}
                  >
                    <td>
                      <div className="cell-main">
                        <AgentAvatar id={a.id} name={a.name} />
                        <div>
                          <Link to="/agentes/$agentId" params={{ agentId: a.id }}>
                            {a.name}
                          </Link>
                          {a.description && <small>{a.description}</small>}
                        </div>
                      </div>
                    </td>
                    <td>
                      <AgentStatusPill status={a.status} />
                    </td>
                    <td className="mono" data-optional>
                      {a.currentVersion ? (
                        `v${a.currentVersion}`
                      ) : (
                        <span className="muted">Sin publicar</span>
                      )}
                    </td>
                    <td className="mono" data-optional data-wide>
                      {a.model}
                    </td>
                    <td className="num" data-optional data-wide>
                      {a.toolCount}
                    </td>
                    <td
                      className="muted nowrap"
                      data-optional
                      title={a.lastRunAt ? dateTime(a.lastRunAt) : undefined}
                    >
                      {a.lastRunAt ? relativeTime(a.lastRunAt) : 'Nunca'}
                    </td>
                  </tr>
                ))}
                {!visible.length && (
                  <tr className="no-results">
                    <td colSpan={6}>
                      Ningún agente coincide con la búsqueda.{' '}
                      <Button
                        variant="link"
                        onClick={() => {
                          setQuery('');
                          setStatus('all');
                        }}
                      >
                        Limpiar filtros
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
